export type ApiError = Error & { status?: number; details?: unknown }

const JSON_HEADERS = { 'content-type': 'application/json' }

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: { ...JSON_HEADERS, ...(init.headers || {}) },
  })
  const isJson = response.headers.get('content-type')?.includes('application/json')
  const body = isJson ? await response.json() : await response.text()
  if (!response.ok) {
    const error = new Error((body as any)?.error || `Request failed (${response.status})`) as ApiError
    error.status = response.status
    error.details = body
    throw error
  }
  return body as T
}

export const api = {
  health: () => request<{ ok: boolean; time: string }>('/api/health'),
  dashboard: () => request<any>('/api/dashboard/summary'),
  campaigns: () => request<any[]>('/api/campaigns'),
  sites: (campaignId?: string) => request<any[]>(`/api/sites${campaignId ? `?campaignId=${encodeURIComponent(campaignId)}` : ''}`),
  plots: (siteId?: string) => request<any[]>(`/api/plots${siteId ? `?siteId=${encodeURIComponent(siteId)}` : ''}`),
  plotObservations: (plotId: string) => request<any[]>(`/api/plots/${encodeURIComponent(plotId)}/observations`),
  inspectionTemplates: () => request<any[]>('/api/inspection-templates'),
  species: () => request<any[]>('/api/species'),
  tags: () => request<any[]>('/api/tags'),
  careTasks: () => request<any[]>('/api/care'),
  pendingVerification: () => request<any[]>('/api/verification/pending'),
  tree: (code: string) => request<any>(`/api/public/trees/${encodeURIComponent(code)}`),
  createCampaign: (payload: unknown) => request('/api/campaigns', { method: 'POST', body: JSON.stringify(payload) }),
  createTagBatch: (payload: unknown) => request<any>('/api/tags/batches', { method: 'POST', body: JSON.stringify(payload) }),
  replaceTag: (payload: unknown) => request<any>('/api/tags/replace', { method: 'POST', body: JSON.stringify(payload) }),
  replaceTree: (payload: unknown) => request<any>('/api/trees/replace', { method: 'POST', body: JSON.stringify(payload) }),
  submitObservation: (payload: unknown, idempotencyKey: string = crypto.randomUUID()) => request<any>('/api/observations', { method: 'POST', body: JSON.stringify(payload), headers: { 'idempotency-key': idempotencyKey } }),
  verify: (id: string, payload: unknown) => request<any>(`/api/verification/${encodeURIComponent(id)}/decision`, { method: 'POST', body: JSON.stringify(payload) }),
  completeCare: (payload: unknown, idempotencyKey: string = crypto.randomUUID()) => request<any>('/api/care/complete', { method: 'POST', body: JSON.stringify(payload), headers: { 'idempotency-key': idempotencyKey } }),
  verifyCare: (id: string, payload: unknown) => request<any>(`/api/care/${encodeURIComponent(id)}/verify`, { method: 'POST', body: JSON.stringify(payload) }),
  createCare: (payload: unknown) => request<any>('/api/care', { method: 'POST', body: JSON.stringify(payload) }),
  createPlotObservation: (payload: unknown) => request<any>('/api/plots/observations', { method: 'POST', body: JSON.stringify(payload) }),
  reportsCampaign: (campaignId: string) => request<any>(`/api/reports/campaigns/${encodeURIComponent(campaignId)}`),
  uploadMedia: async (file: File, owner: { observationId?: string; treeId?: string; purpose?: string }) => {
    const ticket = await request<{ key: string; mediaId: string; uploadUrl: string }>('/api/media/ticket', {
      method: 'POST',
      body: JSON.stringify({ fileName: file.name, contentType: file.type || 'image/jpeg', byteSize: file.size, ...owner }),
    })
    const response = await fetch(ticket.uploadUrl, { method: 'PUT', headers: { 'content-type': file.type || 'image/jpeg' }, body: file })
    if (!response.ok) throw new Error(`Upload failed (${response.status})`)
    return ticket
  },
}
